//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import moment from 'moment-timezone';

import {
  CreatorsCampaignListItem,
  CreatorsCampaignsMember,
} from '@apis/creators-campaigns/CreatorsCampaignsApiModel';
import { CampaignPlatformRaw } from '@apis/creators-campaigns/creators-proposals/CreatorsProposalsModel';
import {
  CampaignsCreatorsSortKey,
  CreatorsCampaignListSortKey,
} from '@campaigns/components/campaign-requests/creator-campaigns/CreatorsCampaignsTypes';
import { CreatorsCampaignsUnifiedMetricsStatsCreatorIdPrefix } from '@campaigns/constants/UnifiedMetricsStatsConstants';
import { SocialCreatorsPlatforms } from '@models/CreatorsTypes';
import { EcommerceProvider } from '@models/MetricsGatewayApiModels';
import { SocialNetwork } from '@models/SocialNetwork';
import { isDefined } from '@utils/filterPredicates';
import { parseSocialText } from '@utils/stringUtils';

/**
 * Accepts a text string like "hello hi   hey" and return array as ["hello", "hi", "hey"]
 * @param text a string text of the social info items separated by space
 * @returns an array of social info items
 */
export const socialTextToListParser = (text: string = '') => {
  return parseSocialText(text).reduce(
    (acc: string[], { value }) => (value.trim() ? acc.concat(value) : acc),
    [],
  );
};

/**
 * Accept an array of strings as ["hello", "hi", "hey"] and return the space joined string "hello hi hey"
 * @param list an array of strings of social text items
 * @returns a space joined string text
 */
export const socialListToTextParser = (list: string[] | undefined) => {
  return list?.join(' ') ?? '';
};

export function getCreatorNetwork(
  platform: CampaignPlatformRaw,
): SocialCreatorsPlatforms {
  switch (platform) {
    case 'VERAINSTAGRAM':
      return SocialNetwork.Instagram;
    case 'VERATIKTOK':
      return SocialNetwork.TikTok;
    case 'VERAYOUTUBE':
      return SocialNetwork.YouTube;
    default:
      return SocialNetwork.Instagram;
  }
}

/**
 * getCreatorDetailsFromUnifiedCampaignStatsCreatorId gets the creator details from
 * the unified campaign stats creatorId that has a format of `platform_username`. If
 * the creatorId is not in the correct format, it will return undefined.
 *
 * @param creatorId a string in the format of `platform_username`
 * @returns the creator details if the creatorId is in the correct format, otherwise undefined
 */
export const getCreatorDetailsFromUnifiedCampaignStatsCreatorId = (
  creatorId?: string,
):
  | {
      username: string;
      network: SocialNetwork;
    }
  | undefined => {
  if (!creatorId) {
    return undefined;
  }

  const [platform, ...usernameParts] = creatorId.split('_');
  const normalizedPlatform = (() => {
    switch (platform) {
      case CreatorsCampaignsUnifiedMetricsStatsCreatorIdPrefix.Instagram:
        return SocialNetwork.Instagram;
      case CreatorsCampaignsUnifiedMetricsStatsCreatorIdPrefix.TikTok:
        return SocialNetwork.TikTok;
      default:
        return undefined;
    }
  })();
  const username = usernameParts.join('_');

  if (!normalizedPlatform || !username) {
    return undefined;
  }

  return {
    username,
    network: normalizedPlatform,
  };
};

export const getValidCampaignEndDate = (
  startDate: Date | undefined,
  endDate: Date | undefined,
) => {
  const now = moment();

  const validEndDate =
    isDefined(startDate) &&
    moment(startDate).isAfter(now) &&
    !isDefined(endDate)
      ? moment(startDate).add(1, 'day').toDate() // add 1 day to endDate
      : moment(endDate ?? now).toDate();

  return validEndDate;
};

export const sortCreatorCampaigns = (
  data: CreatorsCampaignListItem[],
  sortKey: CreatorsCampaignListSortKey,
): CreatorsCampaignListItem[] => {
  const sortedCreators = [...data].sort((a, b) => {
    switch (sortKey) {
      case CreatorsCampaignListSortKey.Creators:
        return (b.creatorsCount || 0) - (a.creatorsCount || 0);
      case CreatorsCampaignListSortKey.Visits:
        return (b.clicks || 0) - (a.clicks || 0);
      case CreatorsCampaignListSortKey.AffiliateRevenue:
        return (b.totalRevenue || 0) - (a.totalRevenue || 0);
      case CreatorsCampaignListSortKey.OnsiteRevenue:
        return (b.onSiteRevenue || 0) - (a.onSiteRevenue || 0);
      default:
        return 0;
    }
  });

  return sortedCreators;
};

export const sortCreatorCampaignsCreators = (
  data: CreatorsCampaignsMember[],
  sortKey: CampaignsCreatorsSortKey,
): CreatorsCampaignsMember[] => {
  if (!data) {
    return [];
  }

  const sortedCreators = [...data].sort((a, b) => {
    const aAggregates = a.creator?.aggregateResponse || {};
    const bAggregates = b.creator?.aggregateResponse || {};
    const aAffiliates = a.affiliate || {};
    const bAffiliates = b.affiliate || {};
    const aAnalytics = a.analytics || {};
    const bAnalytics = b.analytics || {};

    switch (sortKey) {
      case CampaignsCreatorsSortKey.Content:
        return (bAggregates.mediaCount || 0) - (aAggregates.mediaCount || 0);
      case CampaignsCreatorsSortKey.Impressions:
        return (bAggregates.impressions || 0) - (aAggregates.impressions || 0);
      case CampaignsCreatorsSortKey.Engagement:
        return (
          (bAggregates.followerEngagementRate || 0) -
          (aAggregates.followerEngagementRate || 0)
        );
      case CampaignsCreatorsSortKey.EMV:
        return (
          (bAggregates.earnedMediaValue || 0) -
          (aAggregates.earnedMediaValue || 0)
        );
      case CampaignsCreatorsSortKey.Visits:
        return (bAnalytics.visits || 0) - (aAnalytics.visits || 0);
      case CampaignsCreatorsSortKey.AffiliateRevenue:
        return (bAffiliates.revenue || 0) - (aAffiliates.revenue || 0);
      case CampaignsCreatorsSortKey.OnsiteRevenue:
        return (
          (bAnalytics.onSiteRevenue || 0) - (aAnalytics.onSiteRevenue || 0)
        );
      default:
        return 0;
    }
  });

  return sortedCreators;
};

export const hasCreatorCampaignsGAEnabled = (
  ecommerceProvider: string,
  ecommerceTracking: boolean,
) => {
  const isGAConnected =
    ecommerceProvider === EcommerceProvider.GoogleAnalytics ||
    ecommerceProvider === EcommerceProvider.Demo;
  const isEcomEnabled = !!(
    isGAConnected &&
    (ecommerceTracking || ecommerceProvider === EcommerceProvider.Demo)
  );

  return { isGAConnected, isEcomEnabled };
};
